import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  Eye,
  EyeOff,
  Loader2,
  MapPin,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import {
  applyVirtualTourCommand,
  createVirtualTourCommand,
  fetchMerchantVirtualTours,
  uploadVirtualTourMedia,
} from '../../lib/forfeoReserveVirtualTourClient';
import type {
  ForfeoReserveVirtualTourHotspotKind,
  ForfeoReserveVirtualTourMediaKind,
  ForfeoReserveVirtualTourSnapshot,
} from '../../../supabase/functions/_shared/forfeo-reserve-virtual-tour';

type Props = {
  businessId: string;
  businessName: string;
};

export default function ReserveVirtualTourManager({
  businessId,
  businessName,
}: Props) {
  const [tours, setTours] = useState<ForfeoReserveVirtualTourSnapshot[]>([]);
  const [selectedTourId, setSelectedTourId] = useState('');
  const [selectedSceneId, setSelectedSceneId] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [tourTitle, setTourTitle] = useState(`Visite virtuelle — ${businessName}`);
  const [sceneTitle, setSceneTitle] = useState('');
  const [sceneDescription, setSceneDescription] = useState('');
  const [sceneAltText, setSceneAltText] = useState('');
  const [mediaKind, setMediaKind] =
    useState<ForfeoReserveVirtualTourMediaKind>('panorama_image');
  const [hotspotKind, setHotspotKind] =
    useState<ForfeoReserveVirtualTourHotspotKind>('information');
  const [hotspotLabel, setHotspotLabel] = useState('');
  const [hotspotInfo, setHotspotInfo] = useState('');
  const [hotspotX, setHotspotX] = useState(50);
  const [hotspotY, setHotspotY] = useState(50);
  const [targetSceneId, setTargetSceneId] = useState('');
  const [catalogItemId, setCatalogItemId] = useState('');
  const [externalUrl, setExternalUrl] = useState('');

  const selectedTour = useMemo(
    () => tours.find((tour) => tour.tourId === selectedTourId) || tours[0],
    [tours, selectedTourId],
  );
  const selectedScene = useMemo(
    () => selectedTour?.scenes.find((scene) => scene.id === selectedSceneId)
      || selectedTour?.scenes[0],
    [selectedTour, selectedSceneId],
  );

  async function refresh() {
    setBusy(true);
    setMessage('');
    try {
      const result = await fetchMerchantVirtualTours(businessId);
      setTours(result);
      if (!selectedTourId && result[0]) setSelectedTourId(result[0].tourId);
      if (!selectedSceneId && result[0]?.scenes[0]) {
        setSelectedSceneId(result[0].scenes[0].id);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    refresh();
  }, [businessId]);

  async function command(input: Parameters<typeof createVirtualTourCommand>[0]) {
    setBusy(true);
    setMessage('');
    try {
      await applyVirtualTourCommand(createVirtualTourCommand(input));
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  }

  async function createTour() {
    await command({
      kind: 'create_tour',
      businessId,
      title: tourTitle,
      locale: 'fr-CA',
    });
  }

  async function uploadScene(file: File) {
    if (!selectedTour) return;
    setBusy(true);
    setMessage('');
    try {
      const media = await uploadVirtualTourMedia({
        businessId,
        tourId: selectedTour.tourId,
        file,
        mediaKind,
        altText: sceneAltText || sceneTitle || businessName,
      });
      await applyVirtualTourCommand(createVirtualTourCommand({
        kind: 'create_scene',
        businessId,
        tourId: selectedTour.tourId,
        mediaId: media.mediaId,
        mediaKind,
        title: sceneTitle || `Scène ${selectedTour.scenes.length + 1}`,
        description: sceneDescription,
        mediaAltText: sceneAltText || sceneTitle || businessName,
        expectedRevision: selectedTour.revision,
      }));
      setSceneTitle('');
      setSceneDescription('');
      setSceneAltText('');
      await refresh();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error));
      setBusy(false);
    }
  }

  async function createHotspot() {
    if (!selectedTour || !selectedScene) return;
    await command({
      kind: 'create_hotspot',
      businessId,
      tourId: selectedTour.tourId,
      sceneId: selectedScene.id,
      expectedRevision: selectedTour.revision,
      hotspotKind,
      label: hotspotLabel,
      informationText: hotspotKind === 'information' ? hotspotInfo : undefined,
      targetSceneId: hotspotKind === 'scene' ? targetSceneId : undefined,
      catalogItemId: hotspotKind === 'reserve' ? catalogItemId : undefined,
      externalUrl: hotspotKind === 'external' ? externalUrl : undefined,
      xPercent: hotspotX,
      yPercent: hotspotY,
    });
    setHotspotLabel('');
    setHotspotInfo('');
    setExternalUrl('');
  }

  return (
    <section className="space-y-6" data-forfeo-ui-v218="virtual-tour" data-commerce-panel-v219="virtual-tour" data-commerce-v220-panel="virtual-tour">
      <header className="rounded-[2rem] border border-slate-200 bg-white p-7 text-slate-950 shadow-sm">
        <p className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-700">
          Forfeo Reserve
        </p>
        <h2 className="mt-2 text-3xl font-black">Visite virtuelle 360°</h2>
        <p className="mt-3 max-w-3xl text-sm text-slate-700">
          Créez des scènes immersives, ajoutez des points interactifs et dirigez
          les visiteurs vers le parcours officiel Reserve. Aucun prix ou paiement
          n’est géré dans cet outil.
        </p>
      </header>

      {message && (
        <div className="flex gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          <AlertCircle className="shrink-0" />
          <span>{message}</span>
        </div>
      )}

      {busy && (
        <div className="flex items-center gap-2 text-sm font-bold text-violet-700">
          <Loader2 className="animate-spin" size={18} /> Synchronisation…
        </div>
      )}

      {tours.length === 0 ? (
        <div className="rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm">
          <h3 className="text-xl font-black">Créer votre première visite</h3>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row">
            <input
              value={tourTitle}
              onChange={(event) => setTourTitle(event.target.value)}
              className="min-h-12 flex-1 rounded-xl border border-slate-300 px-4"
              maxLength={120}
            />
            <button
              type="button"
              onClick={createTour}
              disabled={busy || tourTitle.trim().length < 2}
              className="inline-flex min-h-12 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 font-black text-white hover:bg-blue-700 disabled:opacity-50"
            >
              <Plus size={18} /> Créer
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-[280px_minmax(0,1fr)]">
          <aside className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="font-black">Visites</h3>
            <div className="mt-4 grid gap-2">
              {tours.map((tour) => (
                <button
                  key={tour.tourId}
                  type="button"
                  onClick={() => {
                    setSelectedTourId(tour.tourId);
                    setSelectedSceneId(tour.scenes[0]?.id || '');
                  }}
                  className={`rounded-xl border p-3 text-left ${
                    selectedTour?.tourId === tour.tourId
                      ? 'border-violet-500 bg-violet-50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <span className="block font-black">{tour.title}</span>
                  <span className="mt-1 inline-flex items-center gap-1 text-xs text-slate-700">
                    {tour.status === 'published' ? <Eye size={13} /> : <EyeOff size={13} />}
                    {tour.status} · {tour.scenes.length} scène(s)
                  </span>
                </button>
              ))}
            </div>
          </aside>

          <div className="space-y-5">
            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h3 className="text-xl font-black">{selectedTour?.title}</h3>
                  <p className="text-sm text-slate-700">
                    Révision {selectedTour?.revision} · {selectedTour?.status}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedTour?.status !== 'published' ? (
                    <button
                      type="button"
                      onClick={() => selectedTour && command({
                        kind: 'publish_tour',
                        businessId,
                        tourId: selectedTour.tourId,
                        expectedRevision: selectedTour.revision,
                      })}
                      disabled={busy || !selectedTour?.scenes.length}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-emerald-600 px-4 font-black text-white disabled:opacity-50"
                    >
                      <Eye size={17} /> Publier
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => selectedTour && command({
                        kind: 'archive_tour',
                        businessId,
                        tourId: selectedTour.tourId,
                        expectedRevision: selectedTour.revision,
                      })}
                      disabled={busy}
                      className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-white px-4 font-black text-slate-900"
                    >
                      <EyeOff size={17} /> Archiver
                    </button>
                  )}
                </div>
              </div>
            </section>

            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="flex items-center gap-2 text-lg font-black">
                <Upload className="text-violet-600" /> Ajouter une scène
              </h3>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <input
                  value={sceneTitle}
                  onChange={(event) => setSceneTitle(event.target.value)}
                  placeholder="Titre de la scène"
                  className="min-h-11 rounded-xl border border-slate-300 px-3"
                  maxLength={120}
                />
                <select
                  value={mediaKind}
                  onChange={(event) =>
                    setMediaKind(event.target.value as ForfeoReserveVirtualTourMediaKind)
                  }
                  className="min-h-11 rounded-xl border border-slate-300 bg-white px-3"
                >
                  <option value="panorama_image">Photo panoramique 360°</option>
                  <option value="standard_image">Photo standard</option>
                  <option value="video">Vidéo</option>
                </select>
                <textarea
                  value={sceneDescription}
                  onChange={(event) => setSceneDescription(event.target.value)}
                  placeholder="Description"
                  className="min-h-24 rounded-xl border border-slate-300 p-3 sm:col-span-2"
                  maxLength={1000}
                />
                <input
                  value={sceneAltText}
                  onChange={(event) => setSceneAltText(event.target.value)}
                  placeholder="Description accessible du média"
                  className="min-h-11 rounded-xl border border-slate-300 px-3 sm:col-span-2"
                  maxLength={240}
                />
                <label className="inline-flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 font-black text-white hover:bg-blue-700 sm:col-span-2">
                  <Upload size={18} />
                  Choisir et téléverser le média
                  <input
                    type="file"
                    className="hidden"
                    accept={
                      mediaKind === 'video'
                        ? 'video/mp4,video/webm'
                        : 'image/jpeg,image/png,image/webp'
                    }
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) uploadScene(file);
                      event.currentTarget.value = '';
                    }}
                  />
                </label>
              </div>
            </section>

            <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
              <h3 className="font-black">Scènes et points interactifs</h3>
              <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
                {selectedTour?.scenes.map((scene) => (
                  <button
                    key={scene.id}
                    type="button"
                    onClick={() => setSelectedSceneId(scene.id)}
                    className={`min-w-40 rounded-xl border p-3 text-left ${
                      selectedScene?.id === scene.id
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-slate-200'
                    }`}
                  >
                    <span className="block font-black">{scene.title}</span>
                    <span className="text-xs text-slate-700">
                      {scene.hotspots.length} point(s)
                    </span>
                  </button>
                ))}
              </div>

              {selectedScene && (
                <div className="mt-6 grid gap-5 lg:grid-cols-2">
                  <div className="overflow-hidden rounded-2xl bg-white">
                    {selectedScene.media.kind === 'video' ? (
                      <video
                        className="aspect-video w-full object-cover"
                        controls
                        src={selectedScene.media.signedUrl}
                      />
                    ) : (
                      <img
                        src={selectedScene.media.signedUrl}
                        alt={selectedScene.media.altText}
                        className="aspect-video w-full object-cover"
                      />
                    )}
                    <div className="p-4 text-white">
                      <p className="font-black">{selectedScene.title}</p>
                      <p className="mt-1 text-xs text-slate-700">
                        {selectedScene.media.kind}
                      </p>
                    </div>
                  </div>

                  <div>
                    <h4 className="flex items-center gap-2 font-black">
                      <MapPin className="text-fuchsia-600" size={18} />
                      Nouveau point interactif
                    </h4>
                    <div className="mt-3 grid gap-3 sm:grid-cols-2">
                      <select
                        value={hotspotKind}
                        onChange={(event) =>
                          setHotspotKind(
                            event.target.value as ForfeoReserveVirtualTourHotspotKind,
                          )
                        }
                        className="min-h-11 rounded-xl border border-slate-300 bg-white px-3"
                      >
                        <option value="information">Information</option>
                        <option value="scene">Changer de scène</option>
                        <option value="reserve">Réserver</option>
                        <option value="external">Lien externe</option>
                      </select>
                      <input
                        value={hotspotLabel}
                        onChange={(event) => setHotspotLabel(event.target.value)}
                        placeholder="Libellé"
                        className="min-h-11 rounded-xl border border-slate-300 px-3"
                        maxLength={100}
                      />
                      <label className="grid gap-1 text-xs font-bold text-slate-700">
                        Position horizontale
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={hotspotX}
                          onChange={(event) => setHotspotX(Number(event.target.value))}
                        />
                      </label>
                      <label className="grid gap-1 text-xs font-bold text-slate-700">
                        Position verticale
                        <input
                          type="range"
                          min={0}
                          max={100}
                          value={hotspotY}
                          onChange={(event) => setHotspotY(Number(event.target.value))}
                        />
                      </label>

                      {hotspotKind === 'information' && (
                        <textarea
                          value={hotspotInfo}
                          onChange={(event) => setHotspotInfo(event.target.value)}
                          placeholder="Texte d’information"
                          className="min-h-24 rounded-xl border border-slate-300 p-3 sm:col-span-2"
                          maxLength={1000}
                        />
                      )}
                      {hotspotKind === 'scene' && (
                        <select
                          value={targetSceneId}
                          onChange={(event) => setTargetSceneId(event.target.value)}
                          className="min-h-11 rounded-xl border border-slate-300 bg-white px-3 sm:col-span-2"
                        >
                          <option value="">Choisir la scène cible</option>
                          {selectedTour?.scenes
                            .filter((scene) => scene.id !== selectedScene.id)
                            .map((scene) => (
                              <option key={scene.id} value={scene.id}>
                                {scene.title}
                              </option>
                            ))}
                        </select>
                      )}
                      {hotspotKind === 'reserve' && (
                        <input
                          value={catalogItemId}
                          onChange={(event) => setCatalogItemId(event.target.value)}
                          placeholder="UUID du service Reserve"
                          className="min-h-11 rounded-xl border border-slate-300 px-3 sm:col-span-2"
                        />
                      )}
                      {hotspotKind === 'external' && (
                        <input
                          value={externalUrl}
                          onChange={(event) => setExternalUrl(event.target.value)}
                          placeholder="https://..."
                          className="min-h-11 rounded-xl border border-slate-300 px-3 sm:col-span-2"
                        />
                      )}

                      <button
                        type="button"
                        onClick={createHotspot}
                        disabled={busy || hotspotLabel.trim().length < 2}
                        className="inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-blue-600 px-4 font-black text-white hover:bg-blue-700 disabled:opacity-50 sm:col-span-2"
                      >
                        <Plus size={17} /> Ajouter le point
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {selectedScene?.hotspots.length ? (
                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  {selectedScene.hotspots.map((hotspot) => (
                    <div
                      key={hotspot.id}
                      className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3"
                    >
                      <div>
                        <p className="font-black">{hotspot.label}</p>
                        <p className="text-xs text-slate-700">
                          {hotspot.kind} · {hotspot.xPercent.toFixed(0)}% /
                          {' '}{hotspot.yPercent.toFixed(0)}%
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => selectedTour && command({
                          kind: 'delete_hotspot',
                          businessId,
                          tourId: selectedTour.tourId,
                          sceneId: selectedScene.id,
                          hotspotId: hotspot.id,
                          expectedRevision: selectedTour.revision,
                        })}
                        className="rounded-lg bg-red-50 p-2 text-red-600"
                        aria-label="Supprimer le point"
                      >
                        <Trash2 size={17} />
                      </button>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          </div>
        </div>
      )}

      <footer className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>Autorités :</strong> les scènes et points interactifs appartiennent
        à Reserve. Les disponibilités sont résolues par Reserve. Les prix,
        paiements, passes et QR demeurent exclusivement dans Forfeo.com.
      </footer>
    </section>
  );
}
